import { createElement } from '../../../core/dom/helpers.js';
import { wireDatePlaceholderOverlay } from './date-placeholder-overlay.js';
import { createDatePicker } from '../../../components/date-picker/date-picker.js';

/** @type {import('../../../contracts/filters.js').FilterDriver} */
export const dateFilterDriver = {
  type: 'date',
  mount(container, ctx) {
    const { def, onChange } = ctx;

    if (def.customPicker) {
      // Custom picker branch — uses LocaleConfig for cross-browser-deterministic
      // formatting. localeConfig is required when customPicker is true; throw
      // loudly so a missing PageServices wiring surfaces immediately.
      if (!ctx.localeConfig) {
        throw new Error('date-filter-driver: customPicker=true requires ctx.localeConfig');
      }
      // No ariaLabel — the visible <label for="filter-${def.key}"> in
      // filter-bar.js already labels the trigger by id. Setting aria-label
      // here would hide that visible label from assistive tech.
      const picker = createDatePicker(container, {
        id: `filter-${def.key}`,
        value: typeof ctx.value === 'string' ? ctx.value : '',
        placeholder: def.placeholder,
        localeConfig: ctx.localeConfig,
        onChange: (iso) => onChange(iso),
      });
      // Adapt the picker (string-only) to FilterDriverHandle's wider value
      // contract. Filter-bar passes single string values for `date` filters;
      // array shapes are guarded out as a defensive no-op.
      return {
        setValue(value) {
          picker.setValue(typeof value === 'string' ? value : '');
        },
        getValue() {
          return picker.getValue();
        },
        destroy() {
          picker.destroy();
        },
      };
    }

    // Native input branch — unchanged from prior behavior. Type-swap overlay
    // for filters that opt into a placeholder string.
    const input = /** @type {HTMLInputElement} */ (createElement('input', {
      type: 'date',
      id: `filter-${def.key}`,
      name: def.key,
    }));
    if (typeof ctx.value === 'string') {
      input.value = ctx.value;
    }
    const syncType = wireDatePlaceholderOverlay(input, def.placeholder);

    input.addEventListener('change', () => {
      syncType();
      onChange(input.value);
    });
    container.appendChild(input);

    return {
      setValue(value) {
        input.value = typeof value === 'string' ? value : '';
        syncType();
      },
      getValue() {
        return input.value;
      },
      destroy() {
        input.remove();
      },
    };
  },
};
